import type { PitchedContent } from "@amzn/ads-discovery-theme-service-typescript-client";
import type {
    Content,
    ContentSalesforceObjectsAssociation,
} from "@amzn/ads-measurement-plm-service-type-script-client";
import { ContentType } from "@amzn/ads-measurement-plm-service-type-script-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";

import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { getPlmApiClient } from "../../api/plmApi/plmApi";
import type { AssociatedPitchedContent } from "../../store/editContent";
import { getAdvertiserId } from "../../utils/permission.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export function usePitchedContentsAction(): [
    AsyncState<AssociatedPitchedContent[]>,
    UseAsyncActions<AssociatedPitchedContent[]>,
] {
    const accountId = getAdvertiserId();

    const [contentsState, contentsAction] = useAsync<
        AssociatedPitchedContent[]
    >(async () => {
        const response = await (
            await getDiscoveryApiClient()
        ).getPitchedContent();

        const notInProgressContents: PitchedContent[] = response.contents ?? [];

        return handleSfActivityWorkflow(notInProgressContents, accountId);
    }, []);

    useAsyncErrorStatusNotification(contentsState);

    return [contentsState, contentsAction];
}

const handleSfActivityWorkflow = async (
    notInProgressContents: PitchedContent[],
    accountId?: string,
): Promise<AssociatedPitchedContent[]> => {
    const contentsNeedingAssociations: Content[] = notInProgressContents.map(
        mapPitchedContentToContent,
    );

    let associationsMap = new Map<
        string,
        ContentSalesforceObjectsAssociation[]
    >();
    if (contentsNeedingAssociations.length) {
        const associationResponse = await (
            await getPlmApiClient()
        ).getContentSfAssociation(contentsNeedingAssociations);

        associationsMap = buildAssociationsMap(associationResponse, accountId);
    }

    const mappedContents = attachAssociations(
        notInProgressContents,
        associationsMap,
    );

    const withFilteredOpps = mappedContents.map((content) =>
        filterContentForAccount(content, accountId),
    );
    return excludeIfNoOppAndNoAssoc(withFilteredOpps);
};

const attachAssociations = (
    pitchedContents: PitchedContent[],
    associationsMap: Map<string, ContentSalesforceObjectsAssociation[]>,
): AssociatedPitchedContent[] => {
    return pitchedContents.map((pitchedContent) => {
        const contentId = pitchedContent.contentId;
        const salesforceAssociations = associationsMap.get(contentId!) ?? [];
        return {
            ...pitchedContent,
            associations: salesforceAssociations,
        };
    });
};

const filterContentForAccount = (
    content: AssociatedPitchedContent,
    accountId?: string,
): AssociatedPitchedContent => {
    if (accountId && content.accountOpportunities) {
        const filteredOpps = content.accountOpportunities.filter(
            (opportunity) => opportunity.accountId === accountId,
        );
        return { ...content, accountOpportunities: filteredOpps };
    }
    return content;
};

const excludeIfNoOppAndNoAssoc = (
    contents: AssociatedPitchedContent[],
): AssociatedPitchedContent[] => {
    return contents.filter((c) => {
        const isAccountOpportunitiesEmpty = isEmpty(c.accountOpportunities);
        const isSalesforceAssociationsEmpty = isEmpty(c.associations);
        return !isAccountOpportunitiesEmpty || !isSalesforceAssociationsEmpty;
    });
};

const buildAssociationsMap = (
    response: {
        contentSalesforceObjectAssociations?: ContentSalesforceObjectsAssociation[];
    },
    accountId?: string,
): Map<string, ContentSalesforceObjectsAssociation[]> => {
    const associationsMap = new Map<
        string,
        ContentSalesforceObjectsAssociation[]
    >();

    response.contentSalesforceObjectAssociations
        ?.filter(
            (
                item,
            ): item is ContentSalesforceObjectsAssociation & {
                contentId: string;
            } =>
                !!item.content?.contentId && accountId !== undefined
                    ? item.salesforceAssociation?.salesforceAccountId ===
                      accountId
                    : false,
        )
        .forEach((item) => {
            const contentId = item.content?.contentId;
            const existing = associationsMap.get(contentId!) ?? [];
            existing.push(item);
            associationsMap.set(contentId!, existing);
        });
    return associationsMap;
};

const mapPitchedContentToContent = (
    pitchedContent: PitchedContent,
): Content => {
    const { contentId, contentType } = pitchedContent;
    const validContentTypes = Object.values(ContentType);

    if (contentType === "PITCH") {
        return {
            contentId,
            contentType: ContentType.PITCHED_CONTENT,
        };
    }
    if (validContentTypes.includes(contentType as ContentType)) {
        return {
            contentId,
            contentType: contentType as ContentType,
        };
    }
    return {
        contentId,
        contentType: undefined,
    };
};
